import React from 'react'
import { Typography, InlineColorPicker, Tooltip, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { BUILDER_V2_INK, BUILDER_V2_BORDER } from '../../util'

interface ColorOptionProps {
  label: string
  value: string
  /** Tooltip shown next to the label, e.g. explaining where a default color comes from. */
  infoText?: string
  /** The "X" badge shown next to the label itself, clearing straight back to `clearColor`. */
  useClearOption?: boolean
  defaultColor?: string
  helpTextForClear?: string
  clearColor?: string
  /** Renders a "Clear"/`footerClearLabel` button inside the color-picker popover itself, separate
   * from (and independent of) the label-level `useClearOption` "X" badge above. */
  useFooterClearOption?: boolean
  footerClearLabel?: string
  onFooterClear?: () => void
  /** Renders a second popover footer button (e.g. "Remove") alongside Clear/Reset. */
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  secondaryActionDisabled?: boolean
  onChange: (hex: string) => void
  classes?: StyleClasses<typeof useStyles>
  presetColors?: string[]
}

const useStyles = makeStyles<Theme, ColorOptionProps>(
  (theme: Theme) => {
    return {
      root: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: 44
      },
      label: {
        fontSize: '.8125rem',
        fontWeight: 500,
        marginRight: theme.spacing(3),
        color: BUILDER_V2_INK
      },
      swatch: {
        width: 26,
        height: 26,
        marginRight: 0,
        borderRadius: 5,
        outline: 'none !important',
        boxShadow: `inset 0 0 0 1px ${BUILDER_V2_BORDER}`
      },
      tooltipRoot: {
        marginLeft: 'auto',
        marginRight: theme.spacing(2)
      },
      infoTooltipRoot: {
        marginLeft: theme.spacing(1)
      },
      tooltipBadge: {
        cursor: 'pointer',
        top: 0,
        right: 0,
        '& svg': {
          fontSize: '1rem'
        }
      }
    }
  },
  { name: 'ColorOption' }
)

export default function ColorOption(props: ColorOptionProps) {
  const classes = useStyles(props)
  const {
    label,
    value,
    infoText,
    useClearOption,
    helpTextForClear,
    defaultColor,
    clearColor,
    onChange,
    useFooterClearOption,
    footerClearLabel,
    onFooterClear,
    secondaryActionLabel,
    onSecondaryAction,
    secondaryActionDisabled,
    presetColors
  } = props

  return (
    <div className={classes.root}>
      <Typography className={classes.label} variant={'h5'}>
        {label}
      </Typography>
      {infoText && (
        <div className={classes.infoTooltipRoot}>
          <Tooltip
            placement={'left'}
            content={infoText}
            icon={<InfoOutlinedIcon />}
            classes={{ badge: classes.tooltipBadge }}
          />
        </div>
      )}
      {useClearOption && value !== defaultColor && (
        <div className={classes.tooltipRoot} onClick={() => onChange(clearColor)}>
          <Tooltip
            placement={'left'}
            content={helpTextForClear}
            icon={<CloseIcon />}
            classes={{ badge: classes.tooltipBadge }}
          />
        </div>
      )}
      <InlineColorPicker
        useResponsiveSwatchBorder={true}
        classes={{ swatch: classes.swatch }}
        swatchOnly={true}
        value={value}
        onChange={(hex) => onChange(hex)}
        presetColors={presetColors}
        useClearOption={useFooterClearOption}
        clearLabel={footerClearLabel}
        clearColor={clearColor}
        defaultColor={defaultColor}
        onClear={onFooterClear}
        secondaryActionLabel={secondaryActionLabel}
        onSecondaryAction={onSecondaryAction}
        secondaryActionDisabled={secondaryActionDisabled}
      />
    </div>
  )
}
